<template>
  <v-navigation-drawer v-model="drawerVisible" temporary stateless fixed right width="500">
    <DrawerToolbar title="Add Tax Info" @close="requestClose" />

    <div v-if="submitted" class="pa-6 d-flex flex-column align-center text-center">
      <Robin animation="flossing" :width="120" :height="120" :loop="true" :show-text="false" />

      <Typography variant="h-4" as="h4" class="mt-4"> Tax Info Submitted! </Typography>

      <Typography variant="body-1" as="p" class="mt-4 mb-0">
        You’re all set! Your tax form will be issued at the end of the year if required.
      </Typography>

      <Typography variant="body-2" as="p" class="mt-4 mb-0 mediumGrey--text">
        Certified on: {{ certifiedOn }}
      </Typography>

      <div class="d-flex justify-center flex-row mt-8" style="gap: 16px">
        <Button v-if="!magicLinkToken" text color="brandPrimary" @click="viewProfile">
          VIEW PROFILE
        </Button>
        <Button color="brandPrimary" @click="forceClose"> CLOSE </Button>
      </div>
    </div>

    <template v-else>
      <!-- Same inline progress state as the Edit Person drawer, shown for the whole
           save - which can run several requests back to back, and card issuance
           as well when issueCard is set. -->
      <div v-if="loading" class="pa-6 mt-4 d-flex flex-column">
        <Typography variant="h-4" as="h2"> Submitting tax info... </Typography>
        <Typography v-if="submittingDetail" variant="body-1" as="p" class="mt-2 mb-0">
          {{ submittingDetail }}
        </Typography>
        <div class="d-flex justify-center mt-4">
          <Robin
            animation="inflatableDance"
            :width="140"
            :height="140"
            :loop="true"
            :show-text="false"
          />
        </div>
      </div>

      <!-- v-show, not v-if: a failed save brings the form back, and it has to come
           back with everything the user entered rather than empty. -->
      <div v-show="!loading" class="pa-6">
        <Typography variant="body-2" as="p" class="v-input mb-5">
          Enter your tax details so we can issue your tax form if required.
        </Typography>

        <Alert v-if="saveError" variant="error" :message="saveError" class="mb-4" />

        <TaxInfoSection
          ref="taxInfoSection"
          :has-tax-info="hasTaxInfo"
          :saved-tax-type-code="savedTaxTypeCode"
          :user-profile="sectionProfile"
          @update="onTaxUpdate"
        />

        <div class="d-flex justify-end flex-row" style="gap: 16px">
          <Button text color="brandPrimary" @click="requestClose"> CANCEL </Button>
          <Button color="brandPrimary" :loading="loading" :disabled="!taxFormValid" @click="save">
            SUBMIT
          </Button>
        </div>
      </div>
    </template>

    <v-dialog v-model="showUnsavedDialog" width="500">
      <v-card rounded="0" class="d-flex justify-center flex-column pa-6">
        <div class="d-flex justify-space-between align-center mx-2 mb-5">
          <v-icon color="error" x-large class="exit-warning-icon mr-4">mdi-alert</v-icon>
          <v-card-title class="word-break align-text-left exit-warning-text">
            Are you sure you want to exit? You will lose any unsaved changes.
          </v-card-title>
        </div>

        <v-card-actions class="mx-12">
          <v-btn color="primary" outlined width="140" @click="showUnsavedDialog = false">
            Go back
          </v-btn>
          <v-spacer />
          <v-btn color="primary" depressed width="140" @click="onDiscardChanges"> Exit </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import Alert from "@/components/generic/Alert.vue";
import Button from "@/components/generic/Button.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Robin from "@/components/Robin.vue";
import TaxInfoSection from "@/components/profile/TaxInfoSection.vue";
import Typography from "@/components/generic/Typography.vue";
import UserService from "@/services/UserService";
import UserServiceV3 from "@/services/UserServiceV3";
import moment from "moment";
import { mapState } from "vuex";

const SOLE_PROPRIETOR_SINGLE_MEMBER_LLC = "SOLE_PROPRIETOR_SINGLE_MEMBER_LLC";
const GENERIC_ERROR = "We could not save your tax info. Please try again.";

export default {
  name: "TaxInfoFormDrawer",
  components: {
    Alert,
    Button,
    DrawerToolbar,
    Robin,
    TaxInfoSection,
    Typography,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // Opt in to issuing a virtual card alongside the save. Off by default so the
    // drawer only requests a card where the user actually asked for one.
    issueCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      saveError: "",
      submitted: false,
      showUnsavedDialog: false,
      certifiedOn: "",
      hasTaxInfo: false,
      savedTaxTypeCode: null,
      taxData: {},
      taxFormValid: false,
      taxFormDirty: false,
    };
  },
  computed: {
    ...mapState(["userProfile", "magicLinkToken"]),
    // Nothing is at risk once the form is saved or untouched.
    hasUnsavedChanges() {
      return !this.submitted && this.taxFormDirty;
    },
    // Card issuance runs inside the same save and is the slow part, so say so.
    submittingDetail() {
      return this.issueCard ? "We're also setting up your card." : undefined;
    },
    // With no tax record on file, a taxpayer type left on the profile shouldn't be restored.
    sectionProfile() {
      if (this.hasTaxInfo) {
        return this.userProfile;
      }
      return { ...this.userProfile, taxpayerType: null };
    },
    drawerVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
  },
  watch: {
    visible(value) {
      if (value) {
        document.addEventListener("mouseup", this.onDocumentMouseUp);
        this.onOpen();
      } else {
        document.removeEventListener("mouseup", this.onDocumentMouseUp);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("mouseup", this.onDocumentMouseUp);
  },
  methods: {
    // The drawer keeps its content mounted, so the section must be reset explicitly on reopen.
    async onOpen() {
      this.saveError = "";
      this.submitted = false;
      this.showUnsavedDialog = false;
      await this.loadTaxStatus();
      this.$refs.taxInfoSection?.resetForm();
    },
    viewProfile() {
      this.close();
      if (this.$route.name !== "profile") {
        this.$router.push({ name: "profile" });
      }
    },
    async loadTaxStatus() {
      try {
        const taxStatus = await UserServiceV3.getTaxInfoStatus();
        this.hasTaxInfo = taxStatus?.result?.hasTaxInfo ?? false;
        this.savedTaxTypeCode = taxStatus?.result?.typeCode ?? null;
      } catch (error) {
        console.error("Error fetching tax info status:", error);
        this.hasTaxInfo = false;
        this.savedTaxTypeCode = null;
      }
    },
    onTaxUpdate({ data, valid, dirty }) {
      this.taxData = data;
      this.taxFormValid = valid;
      this.taxFormDirty = dirty;
    },
    // Strip fields hidden by the selected taxpayer type so abandoned edits aren't persisted.
    stripHiddenTaxFields() {
      if (this.taxData.taxpayerType === "Business") {
        if (this.taxData.taxClassification !== SOLE_PROPRIETOR_SINGLE_MEMBER_LLC) {
          this.taxData.fullLegalName = null;
        } else {
          this.taxData.taxTradeName = null;
        }
      } else {
        this.taxData.taxClassification = null;
        this.taxData.taxBusinessName = null;
        this.taxData.taxTradeName = null;
      }
    },
    // The drawer is `stateless`, so dismissing it is routed through here instead.
    requestClose() {
      // Nothing is closed mid-save. The save carries on regardless, so closing
      // would only hide its outcome - and would first ask about "unsaved changes"
      // that are in fact being saved.
      if (this.loading) {
        return;
      }
      if (this.hasUnsavedChanges) {
        this.showUnsavedDialog = true;
        return;
      }
      this.forceClose();
    },
    forceClose() {
      this.showUnsavedDialog = false;
      this.drawerVisible = false;
    },
    onDiscardChanges() {
      this.forceClose();
    },
    onClickOutside() {
      this.requestClose();
    },
    // `stateless` stops Vuetify closing on its own, so dismissals are detected here.
    onDocumentMouseUp(event) {
      if (!this.visible || this.showUnsavedDialog || !(event.target instanceof Element)) {
        return;
      }

      // Overlaid dialogs and select menus render outside the drawer but are still "inside" it.
      if (event.target.closest(".v-navigation-drawer, .v-dialog__content, .v-menu__content")) {
        return;
      }

      this.requestClose();
    },
    close() {
      this.drawerVisible = false;
    },
    async save() {
      if (!this.taxFormValid) {
        return;
      }

      this.loading = true;
      this.saveError = "";
      this.stripHiddenTaxFields();

      try {
        // Switching SSN <-> EIN invalidates the stored number, so drop it before saving the new one.
        if (
          this.hasTaxInfo &&
          this.savedTaxTypeCode &&
          this.savedTaxTypeCode !== this.taxData.typeCode
        ) {
          await UserServiceV3.clearTaxInfo({
            reason: `Tax type code changed from ${this.savedTaxTypeCode} to ${this.taxData.typeCode}`,
          });
        }

        // The tax number belongs only in the vault call; keep it off the profile endpoint.
        const profileFields = { ...this.taxData };
        delete profileFields.taxId;
        delete profileFields.typeCode;

        await UserService.updateUser(this.userProfile.userId, profileFields, this.magicLinkToken);

        // Carries the card-issuance outcome, which listeners use to decide between
        // showing an error and falling through to the manual card request flow.
        let saveResponse = null;
        if (this.taxData.taxId) {
          saveResponse = await UserServiceV3.saveTaxInfo(
            {
              typeCode: this.taxData.typeCode,
              taxNumber: this.taxData.taxId,
            },
            { issueCard: this.issueCard }
          );
          this.savedTaxTypeCode = this.taxData.typeCode;
          this.hasTaxInfo = true;
        }

        this.$store.dispatch("setUser", this.userProfile.userId);
        this.certifiedOn = moment().format("MM/DD/YYYY");
        this.submitted = true;
        this.$emit("saved", saveResponse);
      } catch (error) {
        this.saveError = error?.response?.data?.message || error?.message || GENERIC_ERROR;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-btn {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
}

.exit-warning-icon {
  flex-shrink: 0;
}

.exit-warning-text {
  flex: 1;
}

.word-break {
  word-break: break-word;
}

/* TaxInfoSection is laid out for the wide profile page. These overrides adapt it to
   the 500px drawer without changing the shared component. */

/* A row holding a single sm-6 field would sit at half width here; the State/Zip
   row has two children and is left alone. */
::v-deep .row > .col-sm-6:only-child {
  flex: 0 0 100%;
  max-width: 100%;
}

/* Drop only the vertical half of the section's pa-1, which adds 8px to every gap. */
::v-deep .v-input.pa-1 {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Taxpayer Type block: :first-child keeps this off the "Tax Address" heading,
   which also carries mb-4 text-left. */
::v-deep .v-form > .mb-4.text-left:first-child {
  margin-bottom: 0 !important;
}
</style>
